import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import last from 'lodash/last';
import set from 'lodash/set';
import { Observable } from 'rxjs/Observable';
import type { Subscription } from 'rxjs/Subscription';
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { NEW_GROUP_ID } from '../../../../common/constants';
import type { Group } from '../../../../common/types';
import createGroupRest from '../../../create-group';
import reorderGroupsRest from '../../../reorder-groups';
import { getGroupSelector, getGroupsSelector, getTicketTypeIdsByGroupId } from '../../selectors';
import type { ImmutableState, ContainerProps } from '../../types';
import type { ActionCallback } from '../types';
import updateNewGroup from '../update-new-group';

const createGroup =
	(callback: ActionCallback<Group | Group[]>) =>
	(
		{ getState, dispatch }: StoreActionApi<ImmutableState>,
		{ projectId, activeStreams }: ContainerProps,
	): Subscription => {
		const state = getState();
		const groupTemplate = getGroupSelector(state, NEW_GROUP_ID);
		const ticketTypeIds = getTicketTypeIdsByGroupId(state, NEW_GROUP_ID);

		// For type safety, shouldn't happen
		if (!groupTemplate || !ticketTypeIds) {
			return Observable.of(undefined).subscribe(callback);
		}

		const subscription = createGroupRest(projectId, {
			name: groupTemplate.name,
			ticketTypeIds,
		})
			.switchMap((newGroup): Observable<Group[]> => {
				// on create success
				callback(newGroup);
				dispatch(updateNewGroup(newGroup, groupTemplate));
				const groupOrder = getGroupsSelector(getState()).map(({ id }: Group) => id);

				if (last(groupOrder) === newGroup.id) {
					return Observable.empty<never>();
				}

				return reorderGroupsRest(projectId, groupOrder).catch((error) => {
					throw set(error, 'response.reorderFailed', true);
				});
			})
			.subscribe(
				(result: Group[]) => {
					// On reorder success
					callback(result);
				},
				(error) => {
					callback(undefined, error);
				},
			);

		activeStreams && activeStreams.add(subscription);

		return subscription;
	};

export default createGroup;
